/**
 * Section and gauge chrome shared by the three admin-portal dashboards
 * (Collateral Monitoring, Risk Dashboard, Compliance Status). Portfolio heads
 * its tile section with the same label (UI run r3, fi-g8-10).
 *
 * A plain component module: no hook, no API service, so a page importing it
 * pulls nothing else into its module graph (see risk-thresholds.ts for why that
 * matters under `vitest --no-isolate`).
 *
 * Before this module the three pages headed their sections three ways: a small
 * uppercase label on Collateral Monitoring, 26 px `size="card"` headings and
 * 40 px `size="section"` headings on the Risk Dashboard, and 40 px headings on
 * Compliance Status, each carrying a `font-sans` class that Heading's level-2
 * serif overrode. The page title lives in the sticky top bar (AppHeader, the
 * page's h1), so every section heading is one small uppercase label, the same
 * treatment the kit's chart cards give their titles and the sibling monitoring
 * pages give their sections.
 *
 * A gauge whose value is withheld or not reported renders in the same tile
 * MetricGauge draws (packages/ui/src/charts/MetricGauge.tsx): the Card
 * primitive's shell and the kit's metric label, METRIC_LABEL_CLASSES. The pages
 * wrapped a padded div in a padded Card, so the stand-in sat 24 px deeper than
 * the gauge beside it. Since GH #1964 the gauge and MetricCard share that label,
 * so the stand-in takes it from the kit rather than copying a class string.
 */

import type { ReactNode } from 'react';
import { Card, METRIC_LABEL_CLASSES } from '@aaimplatform/ui';

/** The uppercase label AreaChart and DonutChart print as their title. */
const LABEL_CLASSES = 'text-label-xs font-sans font-semibold uppercase text-tertiary leading-normal';

/** A dashboard section heading: an h2 in the small uppercase label treatment. */
export function DashboardSectionLabel({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return <h2 className={`${LABEL_CLASSES} mb-3 ${className}`.trim()}>{children}</h2>;
}

/**
 * The stand-in for a MetricGauge whose value the page may not print. `children`
 * is the reason, in words: the withheld-read caption or 'Not reported'.
 */
export function GaugeUnavailable({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="h-full">
      <div className={`${METRIC_LABEL_CLASSES} mb-1`}>{title}</div>
      <div className="py-8 text-center text-tertiary text-sm">{children}</div>
    </Card>
  );
}
